import {ChangeDetectionStrategy, Component, OnInit, signal} from '@angular/core';
import {ChangeDetectorRef} from '@angular/core';
import {EditStateComponent} from '../../../core/components/edit-component';
import {ActivatedRoute} from '@angular/router';
import {takeUntil} from 'rxjs/operators';
import {PropertyModel, PropertyUsages} from '../../../properties/models/property.model';
import {ApiResponse, PRIVILEGES} from 'common-shared';
import {PropertyService} from '../../../properties/property.service';
import {plainToInstance} from "class-transformer";
import {HttpErrorResponse} from "@angular/common/http";

@Component({
  selector: 'mes-web-property-edit-state',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <app-toolbar>
      <app-breadcrumbs>
      </app-breadcrumbs>
      <app-toolbar-edit-buttons [showSave]="PRIVILEGES.EditCommonData | userHasPrivilege" (save)="save()" [saveState$]="saveState$" (delete)="delete()"
                                [deleteState$]="deleteState$" [showDelete]="!isNew && PRIVILEGES.EditCommonData | userHasPrivilege">
      </app-toolbar-edit-buttons>
    </app-toolbar>
    <div class="row mb-3 align-items-center justify-content-center">
      <div class="col-8 m-auto">
        <div class="card">
          <div class="card-body">
            <h5 class="card-title pb-1 d-flex">
              {{property()?.DisplayName}}
              <help-button class="ms-auto mt-n1" [key]="'help.ime.common.specs'"></help-button>
            </h5>
            <mes-web-property-edit *ngIf="property()" (changed)="onChanged($event)" [property]="property()" [response]="response"></mes-web-property-edit>
          </div>
        </div>
      </div>
    </div>
  `
})
export class PropertyEditStateComponent extends EditStateComponent implements OnInit {
  property = signal<PropertyModel>(null);
  response: ApiResponse<any>;
  protected readonly PRIVILEGES = PRIVILEGES;

  constructor(private route: ActivatedRoute, private propertyService: PropertyService, private changeDetector: ChangeDetectorRef) {
    super();
  }

  ngOnInit() {
    this.router.events.pipe(takeUntil(this.componentDestroyed)).subscribe(() => {
      this.property.set(this.route.snapshot.data.entity as PropertyModel);
      if (this.property() == null) {
        this.property.set(PropertyModel.CreateDefault());
        this.isNew = true;
      }
      this.changeDetector.markForCheck();
    });
  }

  delete() {
    this.propertyService.remove(this.property()).subscribe({
      next: value1 => {
        this.router.navigate(['./common/property']).catch(reason => console.error(reason));
      }, error: value1 => {
        this.response = value1.error;
      }
    });
  }

  save() {
    if (this.isNew) {
      this.add();
    } else {
      this.propertyService.save(this.property()).pipe(takeUntil(this.componentDestroyed)).subscribe({next: value => this.onSaveSuccess(value), error: value => this.onSaveFail(value)});
    }
  }

  add() {
    this.propertyService.addNew(this.property()).subscribe({
      next: value1 => {
        this.router.navigate(['./common/property/edit', value1.Id]).catch(reason => console.error(reason));
      }, error: value1 => {
        this.response = value1.error;
      }
    });
  }

  private onSaveSuccess(savedValue: PropertyModel): void {
    this.property.set(savedValue);
    this.onChanged(true);
  }

  private onSaveFail(response: HttpErrorResponse): void {
    const propertyUsages = plainToInstance(PropertyUsages, response.error.data?.Usages);
    if (response.status == 409 && !!propertyUsages) {
      let propertyModel = this.property();
      this.propertyService.showPropertyUsages(propertyUsages, propertyModel).onHide$.subscribe(dialogClosed => {
        if (dialogClosed.data === true) {
          propertyModel.ForceSave = true;
          this.property.set(propertyModel);
          this.propertyService.save(this.property()).pipe(takeUntil(this.componentDestroyed)).subscribe({next: value => this.onSaveSuccess(value), error: value => this.onOtherSaveFail(value)});
        }
      });
    } else {
      this.onOtherSaveFail(response);
    }
  };

  private onOtherSaveFail(response: any): void {
    this.response = response.error;
  }

  onChanged(isPristine: boolean) {
    this.saveState$.next(!isPristine);
  }
}
